import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../shared/api/AxiosAdmin';
import ChoshiiLogo from '../ChoshiiLogo'; // adjust path if needed

type CampaignRow = {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'finished';

  spendTotal: number;
  impressionsTotal: number;
  clicksTotal: number;

  cpm?: number;
  dailyBudget?: number;
  totalBudget?: number;

    adminSuspended?: boolean;
  // ✅ ADD (optional)
  dailySpend?: number;
  deliveryMode?: 'light' | 'balanced' | 'frequent';
  startDate?: string;
  endDate?: string | null;
};

type AdvertiserStatus = {
  completed: boolean;
  approved: boolean;
  suspended: boolean;
};

type AdvertiserInfo = {
  accountType: 'business' | 'individual' | null;
  legalName: string | null;
  country: string | null;
  city: string | null;
  vatId: string | null;
};

type AdvertiserSummary = {
  advertiserId: string;
  advertiserEmail: string | null;
  advertiserProfileName: string | null;

  advertiserStatus: AdvertiserStatus;

  advertiserInfo: AdvertiserInfo; // ✅ ADD

  spendTotal: number;
  impressionsTotal: number;
  clicksTotal: number;
  campaigns: CampaignRow[];
};


type AdminAdsSummaryResponse = {
  totals: {
    spendTotal: number;
    impressionsTotal: number;
    clicksTotal: number;
  };
  advertisers: AdvertiserSummary[];
};

const AdminBillingOverview: React.FC = () => {
  const [data, setData] = useState<AdminAdsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      
      const res = await adminApi.get<AdminAdsSummaryResponse>('/billing/admin/ads-summary');
      setData(res.data);
    } catch (err) {
      console.error('[AdminBillingOverview] load failed', err);
      setError('Failed to load billing overview');
    } finally {
      setLoading(false);
    }
  };

  const suspendAd = async (adId: string) => {
  await adminApi.post(`/billing/ads/${adId}/admin-suspend`);
  await loadSummary(); // refresh UI
};

const unsuspendAd = async (adId: string) => {
  await adminApi.post(`/billing/ads/${adId}/admin-unsuspend`);
  await loadSummary(); // refresh UI
};

  useEffect(() => {
    loadSummary();
  }, []);

  // ---------- Shell styles (matches StartPage/AdminLogin) ----------
  const shellStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
    color: '#f9fafb',
    fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 25px',
    background: 'rgba(2, 6, 23, 0.9)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(148,163,184,0.2)',
  };

  const windowStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 1100,
    borderRadius: 24,
    border: '1px solid rgba(148,163,184,0.3)',
    background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
    boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
    padding: '20px 20px 24px',
    position: 'relative',
  };

  // status chip helper
  const statusChip = (status: CampaignRow['status']) => {
    const base: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: 11,
      border: '1px solid rgba(148,163,184,0.25)',
      background: 'rgba(255,255,255,0.06)',
      color: '#cbd5e1',
    };

    if (status === 'running') return { ...base, background: 'rgba(16,185,129,0.18)', color: '#86efac', borderColor: 'rgba(16,185,129,0.35)' };
    if (status === 'paused') return { ...base, background: 'rgba(245,158,11,0.18)', color: '#fde68a', borderColor: 'rgba(245,158,11,0.35)' };
    return base;
  };

  return (
    <div style={shellStyle}>
      {/* Top bar */}
      <header style={headerStyle}>
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Back to home"
        >
          <ChoshiiLogo
            animateSheen
            fontFamily="'Playfair Display', serif"
            style={{ width: 160 }}
            padding={0}
          />
        </div>

        <div
          style={{
            fontSize: 12,
            color: '#b1b8c7',
            border: '1px solid rgba(148,163,184,0.25)',
            padding: '6px 12px',
            borderRadius: 999,
            background: 'rgba(2,6,23,0.6)',
          }}
        >
          Ads Billing
        </div>
      </header>

      {/* Content */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          padding: 24,
          overflow: 'auto',
        }}
        className="scroll-y"
      >
        <div style={windowStyle}>
          {/* Back arrow to /admin */}
          <button
            type="button"
            onClick={() => navigate('/admin')}
            aria-label="Back to Admin"
            title="Back to Admin"
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="25"
              height="25"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#b1b8c7"
              strokeWidth="2.0"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: 18, marginTop: 12 }}>
            <h1
              style={{
                fontSize: 24,
                marginBottom: 6,
                letterSpacing: -0.03,
                color: '#b1b8c7',
              }}
            >
              Admin – Ads Billing
            </h1>
            <p style={{ fontSize: 13, color: '#b1b8c7' }}>
              Overview of advertiser spend, impressions, and clicks across all campaigns.
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ color: '#b1b8c7', textAlign: 'center', padding: 18 }}>
              Loading billing overview…
            </div>
          )}

          {/* Error / No data */}
          {!loading && !data && (
            <>
              {error && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 10,
                    borderRadius: 10,
                    color: '#fed7aa',
                    background: 'rgba(127,29,29,0.3)',
                    border: '1px solid rgba(248,113,113,0.6)',
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}
              <div style={{ color: '#b1b8c7' }}>No data available.</div>
            </>
          )}

          {/* Data */}
          {!loading && data && (
            <>
              {/* Global totals */}
              <section
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div style={metricCardStyle}>
                  <div style={metricLabelStyle}>Total spend (all time)</div>
                  <div style={metricValueStyle}>€{data.totals.spendTotal.toFixed(2)}</div>
                </div>

                <div style={metricCardStyle}>
                  <div style={metricLabelStyle}>Total impressions</div>
                  <div style={metricValueStyle}>
                    {data.totals.impressionsTotal.toLocaleString()}
                  </div>
                </div>

                <div style={metricCardStyle}>
                  <div style={metricLabelStyle}>Total clicks</div>
                  <div style={metricValueStyle}>
                    {data.totals.clicksTotal.toLocaleString()}
                  </div>
                </div>
              </section>

              {error && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 10,
                    borderRadius: 10,
                    color: '#fed7aa',
                    background: 'rgba(127,29,29,0.3)',
                    border: '1px solid rgba(248,113,113,0.6)',
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              {/* Advertisers */}
              <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {data.advertisers.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#b1b8c7' }}>
                    No advertisers / campaigns yet.
                  </div>
                ) : (
                  data.advertisers.map((adv) => (
                    <div
                      key={adv.advertiserId}
                      style={{
                        borderRadius: 18,
                        border: '1px solid rgba(148,163,184,0.22)',
                        background: 'rgba(2,6,23,0.55)',
                        padding: 14,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.65)',
                      }}
                    >
                      {/* advertiser header */}
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          marginBottom: 12,
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 11, color: 'rgba(177,184,199,0.75)' }}>
                            Advertiser
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#e5e7eb' }}>
                            {adv.advertiserProfileName || adv.advertiserEmail || adv.advertiserId}
                          </div>
                          {adv.advertiserEmail && (
                            <div style={{ fontSize: 12, color: '#b1b8c7' }}>
                              {adv.advertiserEmail}
                            </div>
                          )}
                        </div>

{adv.advertiserInfo?.legalName && (
  <div style={{ fontSize: 12, color: '#9ca3af' }}>
    {adv.advertiserInfo.accountType === 'business'
      ? 'Company'
      : 'Individual'} • {adv.advertiserInfo.legalName}
  </div>
)}

{adv.advertiserInfo?.country && (
  <div style={{ fontSize: 11, color: '#6b7280' }}>
    {adv.advertiserInfo.city}, {adv.advertiserInfo.country}
    {adv.advertiserInfo.vatId && ` • VAT ${adv.advertiserInfo.vatId}`}
  </div>
)}



                        <div style={{ display: 'flex', gap: 16 }}>
                          <div>
                            <div style={miniLabelStyle}>Spend</div>
                            <div style={miniValueStyle}>€{adv.spendTotal.toFixed(2)}</div>
                          </div>
                          <div>
                            <div style={miniLabelStyle}>Impressions</div>
                            <div style={miniValueStyle}>{adv.impressionsTotal.toLocaleString()}</div>
                          </div>
                          <div>
                            <div style={miniLabelStyle}>Clicks</div>
                            <div style={miniValueStyle}>{adv.clicksTotal.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>

                      {/* table */}
                      <div style={{ overflowX: 'auto' }}>
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 13,
                            color: '#cbd5e1',
                          }}
                        >
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.18)' }}>
                              <th style={thStyle}>Campaign</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Spend</th>
                              <th style={thStyle}>Impressions</th>
                              <th style={thStyle}>Clicks</th>
                              <th style={thStyle}>CPM</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adv.campaigns.map((c) => (
                              <tr
                                key={c.id}
                                style={{
                                  borderBottom: '1px solid rgba(148,163,184,0.10)',
                                }}
                              >
                                <td style={tdStyle}>{c.name || '(no name)'}</td>
                                <td style={tdStyle}>
                                  <span style={statusChip(c.status)}>{c.status}</span>
                                </td>
                                <td style={tdStyle}>€{c.spendTotal.toFixed(2)}</td>
                                <td style={tdStyle}>{c.impressionsTotal.toLocaleString()}</td>
                                <td style={tdStyle}>{c.clicksTotal.toLocaleString()}</td>
                                <td style={tdStyle}>
  <div style={{ display: 'flex', gap: 8 }}>
    {c.adminSuspended ? (
      <button
        onClick={() => unsuspendAd(c.id)}
        style={{
          fontSize: 11,
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px solid rgba(16,185,129,0.4)',
          background: 'rgba(16,185,129,0.15)',
          color: '#86efac',
          cursor: 'pointer',
        }}
      >
        Unsuspend
      </button>
    ) : (
      <button
        onClick={() => suspendAd(c.id)}
        style={{
          fontSize: 11,
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px solid rgba(248,113,113,0.5)',
          background: 'rgba(248,113,113,0.15)',
          color: '#fecaca',
          cursor: 'pointer',
        }}
      >
        Suspend
      </button>
    )}
  </div>
</td>

                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </section>

              <div
                style={{
                  marginTop: 14,
                  height: 1,
                  width: '100%',
                  background: 'linear-gradient(to right, transparent, #1f2937, transparent)',
                }}
              />

              <p style={{ marginTop: 12, textAlign: 'center', fontSize: 11, color: '#6b7280' }}>
                © {new Date().getFullYear()} • Admin access only
              </p>
            </>
          )}

          {/* simple responsive tweak for totals */}
          <style>{`
            @media (max-width: 780px) {
              .totals-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
        </div>
      </main>
    </div>
  );
};

const metricCardStyle: React.CSSProperties = {
  borderRadius: 16,
  border: '1px solid rgba(148,163,184,0.22)',
  background: 'rgba(2,6,23,0.55)',
  padding: 14,
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(177,184,199,0.75)',
  marginBottom: 6,
};

const metricValueStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: -0.02,
  color: '#e5e7eb',
};

const miniLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(177,184,199,0.75)',
};

const miniValueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#e5e7eb',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 10px 8px 0',
  fontSize: 11,
  color: 'rgba(177,184,199,0.85)',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 10px 10px 0',
  color: '#cbd5e1',
  verticalAlign: 'top',
  whiteSpace: 'nowrap',
};

export default AdminBillingOverview;
